import React from 'react';
import { IconButton, Paper, Typography, Divider, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import { useTranslation } from 'react-i18next';
import { getWmoInfo } from '../../utils/wmoWeather';
import type { CityWeather } from '../../services/lifeCenterService';

interface Props {
  city: CityWeather | null;
  position?: { x: number; y: number } | null;
  onClose: () => void;
  isInMyCity?: boolean;
  onToggleMyCity?: () => void;
}

const CARD_W = 228;
const CARD_H = 220;

const WeatherDetailCard: React.FC<Props> = ({
  city, position, onClose, isInMyCity = false, onToggleMyCity,
}) => {
  const { t, i18n } = useTranslation();
  if (!city) return null;

  const cityName = i18n.language === 'en' ? (city.nameEn || city.name) : city.name;
  const { label } = getWmoInfo(city.weatherCode, i18n.language);
  const lang = i18n.language;

  let posStyle: React.CSSProperties;
  if (position) {
    const GAP = 14;
    let left = position.x + GAP;
    let top  = position.y - CARD_H / 2;
    if (left + CARD_W > window.innerWidth  - 8) left = position.x - CARD_W - GAP;
    if (left < 8)                               left = 8;
    if (top + CARD_H > window.innerHeight - 8)  top  = window.innerHeight - CARD_H - 8;
    if (top < 8)                                top  = 8;
    posStyle = { position: 'absolute', left, top };
  } else {
    posStyle = { position: 'absolute', bottom: 24, left: 24 };
  }

  return (
    <Paper elevation={4} sx={{
      ...posStyle,
      width: CARD_W, p: 2, borderRadius: 3,
      background: 'rgba(15,25,50,0.88)', backdropFilter: 'blur(10px)',
      color: '#fff', zIndex: 10,
    }}>
      {/* Top-right action buttons */}
      <div style={{ position: 'absolute', top: 4, right: 4, display: 'flex', alignItems: 'center' }}>
        {onToggleMyCity && (
          <Tooltip title={isInMyCity
            ? t('lifeCenter.removeFromMyCities')
            : t('lifeCenter.addToMyCities')
          }>
            <IconButton onClick={onToggleMyCity} size="small" sx={{
              color: isInMyCity ? '#ffd54f' : 'rgba(255,255,255,0.45)',
              '&:hover': { color: isInMyCity ? '#ffb300' : 'rgba(255,255,255,0.85)' },
            }}>
              {isInMyCity ? <BookmarkIcon fontSize="small" /> : <BookmarkBorderIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
        <IconButton onClick={onClose} size="small"
          sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#fff' } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, pr: onToggleMyCity ? 7 : 4 }}>
        {city.emoji} {cityName}
      </Typography>
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
        {label}
      </Typography>

      <Typography variant="h3" sx={{ fontWeight: 800, my: 1, lineHeight: 1 }}>
        {city.temp}°
      </Typography>

      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 1 }}>
        {t('lifeCenter.feelsLike')} {city.apparentTemp}°
        {t('lifeCenter.high')} {city.tempMax}°
        {t('lifeCenter.low')} {city.tempMin}°
      </Typography>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)', mb: 1 }} />

      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
        💧 {t('lifeCenter.humidity')} {city.humidity}%
      </Typography>
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
        💨 {t('lifeCenter.wind')} {city.windSpeed} km/h
      </Typography>
    </Paper>
  );
};

export default WeatherDetailCard;
